"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  author: "Sandra P.",
  role: "Geschäftsführerin",
  company: "Trendy Widgets",
  quote: "\"medienschmiede.ai hat unser Marketing transformiert - mit beeindruckenden KI-generierten Visuals und Videos, die unsere Zielgruppe fesseln. Mit beeindruckender Effizienz und Qualität.\"",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/avatar_jane_smith.png",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/logo_trendy_widgets.png"
}, {
  id: 2,
  author: "Jan K.",
  role: "Inhaber",
  company: "Bright Solutions",
  quote: "\"Die erstellten Lerninhalte haben uns geholfen, unser Team effizient und kosteneffektiv zu schulen. Dank des KI-gestützten Workflows konnten wir die Trainingsmodule kurzfristig umsetzen lassen.\"",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/avatar_john_doe.png",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/logo_bright_solutions.png"
}, {
  id: 3,
  author: "Katharina F.",
  role: "Inhaberin",
  company: "NextGen Innovations",
  quote: "\"Die kurzen Bearbeitungszeiten und die konsistente Markenidentität über alle Medien-inhalte hinweg, waren ein Gamechanger für unsere Produkeinführung. Klare Empfehlung.\"",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/avatar_elena_rodriguez.png",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/logo_nextgen_innovations.png"
}, {
  id: 4,
  author: "Matthias W.",
  role: "Geschäftsführer",
  company: "StartUp Flow",
  quote: "\"Wir brauchten einen Partner, der mit unserem schnellen Wachstum Schritt hält. medien-schmiede.ai hat maßgeschneiderte Lösungen geliefert, mit denen wir unsere Zielgruppe erreichen wie nie zuvor.\"",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/avatar_michael_chang.png",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/logo_startup_flow.png"
}];
export default function PartnerPerspectives() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    setProgress((api.selectedScrollSnap() + 1) / api.scrollSnapList().length * 100);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
      setProgress((api.selectedScrollSnap() + 1) / api.scrollSnapList().length * 100);
    });
  }, [api]);
  const handlePrev = () => {
    api?.scrollPrev();
  };
  const handleNext = () => {
    api?.scrollNext();
  };
  return <section id="partner-perspectives" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555638/img/grid_pattern.png')] opacity-[0.03]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl mb-4">Kundenstimmen</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">Mach Dir ein Bild wie unsere Kunden KI-gestützten Medieninhalten verwenden, um ihr Wachstum zu beschleunigen und Kosten zu sparen.</p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/2">
                  <div className="h-full p-1" data-index={index}>
                    <Card className="h-full border-white/10 bg-white/5 backdrop-blur-md shadow-xl transition-all duration-300 hover:border-primary/30 hover:shadow-primary/5">
                      <CardContent className="flex flex-col h-full p-8 gap-6">
                        {}
                        <div className="flex items-center justify-between border-b border-white/5 pb-6">
                          <div className="flex items-center gap-4">
                            <SafeHydrate><Avatar className="h-14 w-14 border-2 border-primary/20 ring-2 ring-primary/10">
                              <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                              <AvatarFallback className="bg-primary/20 text-primary font-bold">
                                {testimonial.author.split(" ").map(n => n[0]).join("")}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>
                            <div>
                              <h4 className="font-semibold text-foreground leading-tight">
                                {testimonial.author}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {testimonial.role}
                              </p>
                            </div>
                          </div>
                          <div className="hidden sm:block opacity-50 grayscale hover:grayscale-0 transition-all duration-300">
                            
                          </div>
                        </div>

                        {}
                        <div className="flex-1 relative">
                          <Quote className="absolute -top-2 -left-2 h-8 w-8 text-primary/10 -z-10 rotate-180" />
                          <blockquote className="font-sans italic text-lg text-foreground/90 leading-relaxed pl-4 border-l-2 border-primary/40">
                            "{testimonial.quote}"
                          </blockquote>
                        </div>

                        {}
                        <div className="sm:hidden mt-auto pt-4 border-t border-white/5 flex justify-end opacity-50">
                          <img src={testimonial.logo} alt={`${testimonial.company} logo`} className="h-6 w-auto object-contain grayscale" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="mt-12 flex flex-col items-center gap-8">
            {}
            <div className="flex items-center gap-6">
              <Button variant="outline" size="icon" onClick={handlePrev} className="h-12 w-12 rounded-full border-primary/30 bg-background/50 text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:border-primary transition-all duration-300 group" aria-label="Previous slide">
                <ChevronLeft className="h-6 w-6 transition-transform group-hover:-translate-x-0.5" />
              </Button>
              
              <div className="w-64 px-4">
                 <Progress value={progress} className="h-1.5 bg-white/10" />
              </div>

              <Button variant="outline" size="icon" onClick={handleNext} className="h-12 w-12 rounded-full border-primary/30 bg-background/50 text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:border-primary transition-all duration-300 group" aria-label="Next slide">
                <ChevronRight className="h-6 w-6 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground font-mono">
                {current.toString().padStart(2, '0')} / {count.toString().padStart(2, '0')}
            </div>
          </div>
        </div>
      </div>
    </section>;
}